import { useCallback, useMemo, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import {
  ArrowLeftIcon,
  ArrowTopRightOnSquareIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline'
import { apiService } from '../../../shared/services/api'
import { useAuth } from '../../../shared/components/ProtectedRoute'
import UserHeader from '../components/UserHeader'
import AnalyticsFilters from '../components/analytics/AnalyticsFilters'
import BreakdownList from '../components/analytics/BreakdownList'
import ClicksTimeSeries from '../components/analytics/ClicksTimeSeries'
import HourlyHeatmap from '../components/analytics/HourlyHeatmap'
import LinkPicker from '../components/analytics/LinkPicker'
import RecentClicksTable from '../components/analytics/RecentClicksTable'
import AnalyticsSummary from '../components/analytics/AnalyticsSummary'
import { Check, Copy, FileText } from 'lucide-react'
import {
  AppWindow,
  Cpu,
  Globe,
  Languages,
  MapPin,
  Megaphone,
  MonitorSmartphone,
  Signpost,
} from 'lucide-react'
import { truncateMiddle } from '../../../shared/lib/analytics-format'
import { languageLabel } from '../../../shared/lib/language-names'
import { translateBreakdownLabel } from '../../../shared/lib/analytics-labels'
import { useTranslation } from 'react-i18next'
import type {
  AnalyticsRange,
  BreakdownRow,
  CsvDataset,
} from '../../../shared/types/analytics'

const RANGE_VALUES: AnalyticsRange[] = ['1d', '7d', '30d', '90d']
const REFETCH_INTERVAL_MS = 300_000
const COPY_FEEDBACK_MS = 1500
const URL_PREVIEW_LENGTH = 72

function parseRange(value: string | null): AnalyticsRange {
  return RANGE_VALUES.includes(value as AnalyticsRange)
    ? (value as AnalyticsRange)
    : '7d'
}

/** 링크 단위 정밀 분석 화면 */
export default function LinkAnalyticsPage() {
  const [copied, setCopied] = useState(false)
  const { t } = useTranslation()

  /** 'Unknown' / 'Direct' 만 화면 언어로 옮긴다 */
  const localize = useCallback(
    (rows: BreakdownRow[]) =>
      rows.map(row => ({
        ...row,
        label: translateBreakdownLabel(row.label, t),
      })),
    [t]
  )

  const { shortCode = '' } = useParams<{ shortCode: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()
  // 기간·봇 포함 여부는 URL 에 유지해 공유·새로고침에도 상태가 보존된다
  const [searchParams, setSearchParams] = useSearchParams()
  const [isExporting, setIsExporting] = useState(false)

  const range = parseRange(searchParams.get('range'))
  const includeBots = searchParams.get('includeBots') === 'true'
  const timeZone = useMemo(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Seoul',
    []
  )

  const updateParams = useCallback(
    (next: Record<string, string | null>) => {
      const params = new URLSearchParams(searchParams)
      Object.entries(next).forEach(([key, value]) => {
        if (value === null) {
          params.delete(key)
        } else {
          params.set(key, value)
        }
      })
      setSearchParams(params, { replace: true })
    },
    [searchParams, setSearchParams]
  )

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['analytics', 'link', shortCode, range, includeBots, timeZone],
    queryFn: () =>
      apiService.getLinkAnalytics(shortCode, {
        range,
        tz: timeZone,
        includeBots,
      }),
    enabled: !!user && !!shortCode,
    refetchInterval: REFETCH_INTERVAL_MS,
    retry: false,
  })

  /** 단축 주소 복사. 성공 표시는 잠깐만 띄운다 */
  const handleCopyShortUrl = useCallback(async () => {
    const code = data?.url.shortCode ?? shortCode
    try {
      await navigator.clipboard.writeText(`https://shorty.kr/${code}`)
      setCopied(true)
      setTimeout(() => setCopied(false), COPY_FEEDBACK_MS)
    } catch (error) {
      console.error('클립보드 복사 실패:', error)
      toast.error(t('analyticsPage.copyError'))
    }
  }, [data?.url.shortCode, shortCode, t])

  const handleExport = async (dataset: CsvDataset) => {
    setIsExporting(true)
    try {
      const blob = await apiService.downloadLinkAnalyticsCsv(
        shortCode,
        dataset,
        { range, tz: timeZone, includeBots }
      )
      const objectUrl = URL.createObjectURL(blob)
      const anchor = document.createElement('a')
      anchor.href = objectUrl
      anchor.download = `${shortCode}-${dataset}-${range}.csv`
      anchor.click()
      URL.revokeObjectURL(objectUrl)
    } catch (downloadError) {
      console.error('CSV 내보내기 실패:', downloadError)
      toast.error(t('analyticsPage.exportError'))
    } finally {
      setIsExporting(false)
    }
  }

  if (isError) {
    const status = (error as { statusCode?: number })?.statusCode
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <UserHeader
          title={t('analyticsPage.linkTitle')}
          subtitle={`/${shortCode}`}
        />
        <div className="container mx-auto px-6 py-16">
          <div className="mx-auto max-w-md rounded-2xl border border-gray-200 bg-white p-8 text-center dark:border-gray-700 dark:bg-gray-800">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              {status === 404
                ? t('analyticsPage.notFound')
                : t('analyticsPage.loadError')}
            </h2>
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              {status === 404
                ? t('analyticsPage.notFoundHint')
                : t('analyticsPage.retryHint')}
            </p>
            <Link
              to="/analytics"
              className="mt-6 inline-flex items-center gap-2 rounded-xl bg-primary-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-primary-700"
            >
              <ArrowLeftIcon className="h-4 w-4" />
              {t('analyticsPage.backHome')}
            </Link>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <UserHeader
        title={t('analyticsPage.linkTitle')}
        subtitle={data?.url.title || `/${shortCode}`}
      />

      <div className="container mx-auto space-y-6 px-6 py-8">
        <Link
          to="/analytics"
          className="inline-flex items-center gap-1.5 self-start text-[13px] font-semibold text-gray-500 transition hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          {t('analyticsPage.backToAll')}
        </Link>

        {/* 링크 머리말 — 제목과 주소를 먼저 보여 주고, 전환은 오른쪽에 둔다 */}
        <section className="flex flex-wrap items-center gap-5">
          <span className="grid h-14 w-14 shrink-0 place-items-center rounded-2xl bg-primary-50 text-primary-600 dark:bg-primary-950/60 dark:text-primary-400">
            <FileText className="h-7 w-7" />
          </span>

          <div className="flex min-w-0 flex-[1_1_360px] flex-col gap-1.5">
            <h2 className="truncate text-[28px] font-extrabold tracking-tight text-gray-900 dark:text-white">
              {data?.url.title || `/${shortCode}`}
            </h2>
            <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1.5 text-sm">
              <button
                type="button"
                onClick={handleCopyShortUrl}
                className="inline-flex h-7 items-center gap-1.5 rounded-lg border border-primary-100 bg-primary-50 px-2.5 font-mono text-[13px] font-bold text-primary-700 transition hover:border-primary-300 dark:border-primary-900 dark:bg-primary-950/50 dark:text-primary-300"
              >
                shorty.kr/{data?.url.shortCode || shortCode}
                {copied ? (
                  <Check className="h-3.5 w-3.5" />
                ) : (
                  <Copy className="h-3.5 w-3.5" />
                )}
              </button>
              {data && (
                <a
                  href={data.url.originalUrl}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="inline-flex min-w-0 items-center gap-1 text-gray-500 transition hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
                >
                  <span className="truncate">
                    {truncateMiddle(data.url.originalUrl, URL_PREVIEW_LENGTH)}
                  </span>
                  <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5 shrink-0" />
                </a>
              )}
            </div>
          </div>

          <div className="w-full sm:w-[300px]">
            <LinkPicker
              selectedShortCode={shortCode}
              onSelect={code =>
                navigate(`/analytics/${code}?${searchParams.toString()}`)
              }
              placeholder={t('analyticsPage.switchLink')}
            />
          </div>
        </section>

        {/* 필터 + 내보내기 */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <AnalyticsFilters
            range={range}
            onRangeChange={value => updateParams({ range: value })}
            includeBots={includeBots}
            onIncludeBotsChange={value =>
              updateParams({ includeBots: value ? 'true' : null })
            }
          />

          <div className="flex items-center gap-2">
            {(
              [
                ['timeseries', t('analyticsPage.exportTimeseries')],
                ['breakdown', t('analyticsPage.exportBreakdown')],
                ['clicks', t('analyticsPage.exportClicks')],
              ] as Array<[CsvDataset, string]>
            ).map(([dataset, label]) => (
              <button
                key={dataset}
                type="button"
                disabled={isExporting || !data}
                onClick={() => handleExport(dataset)}
                className="inline-flex items-center gap-1.5 rounded-xl border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 transition hover:border-primary-400 hover:text-primary-600 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
              >
                <ArrowDownTrayIcon className="h-4 w-4" />
                {label}
              </button>
            ))}
          </div>
        </div>

        {isLoading || !data ? (
          <div className="animate-pulse space-y-6">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
              {[...Array(4)].map((_, index) => (
                <div
                  key={index}
                  className="h-32 rounded-2xl bg-gray-200 dark:bg-gray-700"
                />
              ))}
            </div>
            <div className="h-80 rounded-2xl bg-gray-200 dark:bg-gray-700" />
          </div>
        ) : (
          <>
            <AnalyticsSummary
              cells={[
                {
                  label: t('analyticsPage.periodClicks'),
                  value: data.summary.clicks.toLocaleString(),
                  change: data.summary.clicksChange,
                  hint: t('analyticsPage.previousPeriod', {
                    count: data.summary.previousClicks.toLocaleString(),
                  }),
                },
                {
                  label: t('analyticsPage.uniqueVisitors'),
                  value: data.summary.uniqueClicks.toLocaleString(),
                  change: data.summary.uniqueClicksChange,
                  hint: t('analyticsPage.vsPrevious'),
                },
                {
                  label: t('analyticsPage.returningRate'),
                  value: `${data.summary.returningRate}%`,
                  change: 0,
                  hint: t('analyticsPage.totalAllTime', {
                    count: data.summary.totalClicksAllTime.toLocaleString(),
                  }),
                },
              ]}
            />

            <ClicksTimeSeries
              data={data.timeSeries}
              granularity={range === '1d' ? 'hour' : 'day'}
            />

            {/* 카드 수가 데이터에 따라 달라지므로 칸 수를 고정하지 않는다 */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
              <BreakdownList
                icon={Signpost}
                title={t('analyticsPage.referrers')}
                rows={localize(data.breakdowns.referrers)}
              />
              <BreakdownList
                icon={Globe}
                title={t('analyticsPage.countries')}
                rows={localize(data.breakdowns.countries)}
              />
              <BreakdownList
                icon={MapPin}
                title={t('analyticsPage.cities')}
                rows={localize(data.breakdowns.cities)}
              />
              <BreakdownList
                icon={MonitorSmartphone}
                title={t('analyticsPage.devices')}
                rows={localize(data.breakdowns.devices)}
              />
              <BreakdownList
                icon={AppWindow}
                title={t('analyticsPage.browsers')}
                rows={localize(data.breakdowns.browsers)}
              />
              <BreakdownList
                icon={Cpu}
                title={t('analyticsPage.operatingSystems')}
                rows={localize(data.breakdowns.operatingSystems)}
              />
              <BreakdownList
                icon={Languages}
                title={t('analyticsPage.languages')}
                rows={localize(data.breakdowns.languages).map(row => ({
                  ...row,
                  label:
                    row.label === t('analytics.unknown')
                      ? row.label
                      : languageLabel(row.label),
                }))}
                emptyMessage={t('analyticsPage.noLanguage')}
              />
              <BreakdownList
                icon={Megaphone}
                title={t('analyticsPage.campaign')}
                rows={localize(data.breakdowns.campaigns)}
                emptyMessage={t('analyticsPage.noCampaign')}
              />
              <BreakdownList
                icon={Megaphone}
                title={t('analyticsPage.source')}
                rows={localize(data.breakdowns.sources)}
                emptyMessage={t('analyticsPage.noSource')}
              />
              <BreakdownList
                icon={Megaphone}
                title={t('analyticsPage.medium')}
                rows={localize(data.breakdowns.mediums)}
                emptyMessage={t('analyticsPage.noMedium')}
              />
            </div>

            <HourlyHeatmap cells={data.heatmap} timeZone={data.timeZone} />

            <RecentClicksTable
              clicks={data.recentClicks}
              timeZone={data.timeZone}
            />
          </>
        )}
      </div>
    </div>
  )
}
